import { Injectable, computed, inject, signal } from '@angular/core';
import { RbacService } from '../../services/rbac/permission.service';
import { PROJECT_DETAILS_TAB_KEYS, type ProjectDetailsTabKey } from './project-details-tab.config';

const PERMISSIONS_KEY = 'permissions';

/** Resource Allocation top-level module id (`modules[].id`). */
export const RESOURCE_ALLOCATION_MODULE_ID = 14;

/** Sub-module ids under Resource Allocation (module 13). */
export const RA_SUBMODULE_IDS = {
  DOCUMENTS: 39,
  PROJECT_FEEDBACK: 40,
  DIRECTORY: 41,
  ADDITIONAL_NOTES: 42,
  INSIGHTS: 43,
} as const;

/** Action ids under Resource Allocation sub-modules. */
export const RA_ACTION_IDS = {
  DOCUMENTS_VIEW: 215,
  DOCUMENTS_MANAGE: 216,
  FEEDBACK_VIEW: 217,
  FEEDBACK_MANAGE: 218,
  DIRECTORY_VIEW: 219,
  DIRECTORY_MANAGE: 220,
  NOTES_VIEW: 221,
  NOTES_MANAGE: 222,
  INSIGHTS_VIEW: 223,
} as const;

/** Normalize submodule labels from API → canonical id. */
const SUBMODULE_NAME_TO_ID: Readonly<Record<string, number>> = {
  documents: RA_SUBMODULE_IDS.DOCUMENTS,
  'project feedback': RA_SUBMODULE_IDS.PROJECT_FEEDBACK,
  projectfeedback: RA_SUBMODULE_IDS.PROJECT_FEEDBACK,
  directory: RA_SUBMODULE_IDS.DIRECTORY,
  'additional notes': RA_SUBMODULE_IDS.ADDITIONAL_NOTES,
  additionalnotes: RA_SUBMODULE_IDS.ADDITIONAL_NOTES,
  insights: RA_SUBMODULE_IDS.INSIGHTS,
};

interface RawAction {
  readonly id?: number;
  readonly actionId?: number;
  readonly actionName?: string;
}

interface RawSubModule {
  readonly id?: number;
  readonly submoduleId?: number;
  readonly subModuleName?: string;
  readonly submoduleName?: string;
  readonly name?: string;
  readonly actions?: readonly RawAction[];
}

interface RawModule {
  readonly id?: number;
  readonly moduleId?: number;
  readonly moduleName?: string;
  readonly hasAccess?: boolean;
  readonly subModules?: readonly RawSubModule[];
  readonly submodules?: readonly RawSubModule[];
}

interface RawPermissions {
  readonly modules?: readonly RawModule[];
  readonly issystemRole?: boolean;
}

/** Tabs gated by Resource Allocation view permissions. */
const GATED_TAB_TO_SUBMODULE: ReadonlyMap<ProjectDetailsTabKey, number> = new Map([
  [PROJECT_DETAILS_TAB_KEYS.Documents, RA_SUBMODULE_IDS.DOCUMENTS],
  [PROJECT_DETAILS_TAB_KEYS.Feedback, RA_SUBMODULE_IDS.PROJECT_FEEDBACK],
  [PROJECT_DETAILS_TAB_KEYS.Directory, RA_SUBMODULE_IDS.DIRECTORY],
  [PROJECT_DETAILS_TAB_KEYS.ProjectNotes, RA_SUBMODULE_IDS.ADDITIONAL_NOTES],
  [PROJECT_DETAILS_TAB_KEYS.Insights, RA_SUBMODULE_IDS.INSIGHTS],
]);

/** Child route path → gated tab key (for route guards / deep links). */
const ROUTE_TO_GATED_TAB: ReadonlyMap<string, ProjectDetailsTabKey> = new Map([
  ['documents', PROJECT_DETAILS_TAB_KEYS.Documents],
  ['files', PROJECT_DETAILS_TAB_KEYS.Documents],
  ['feedback', PROJECT_DETAILS_TAB_KEYS.Feedback],
  ['directory', PROJECT_DETAILS_TAB_KEYS.Directory],
  ['project-notes', PROJECT_DETAILS_TAB_KEYS.ProjectNotes],
  ['insights', PROJECT_DETAILS_TAB_KEYS.Insights],
]);

/**
 * Signal store for Resource Allocation tab access on project details.
 * Gates Documents, Project Feedback, Directory, Additional Notes, and Insights
 * by view/manage actions under module id 13. Organization / super admins bypass.
 * All other project tabs are intentionally unaffected.
 */
@Injectable({ providedIn: 'root' })
export class ResourceAllocationAccessService {
  private readonly rbac = inject(RbacService);

  private readonly _permissions = signal<RawPermissions | null>(null);
  private readonly _isAdmin = signal(false);

  constructor() {
    this.refresh();
  }

  /** Re-read admin state + permissions from storage (call after login / permission changes). */
  refresh(): void {
    this._isAdmin.set(this.resolveAdminBypass());
    this._permissions.set(this.readPermissions());
  }

  private readonly grantedActionsBySubmodule = computed<
    ReadonlyMap<number, ReadonlySet<string>>
  >(() => {
    const modules = this._permissions()?.modules ?? [];
    const ra = modules.find((m) => this.isResourceAllocationModule(m));
    if (!ra || ra.hasAccess !== true) {
      return new Map();
    }

    const subs = ra.subModules ?? ra.submodules ?? [];
    const map = new Map<number, Set<string>>();
    for (const sub of subs) {
      const sid = this.resolveSubmoduleId(sub);
      if (sid == null) {
        continue;
      }
      const keys = map.get(sid) ?? new Set<string>();
      for (const action of sub.actions ?? []) {
        const aid = action.id ?? action.actionId;
        if (aid != null) {
          keys.add(`id:${aid}`);
        }
        const name = String(action.actionName ?? '')
          .trim()
          .toLowerCase();
        if (name) {
          keys.add(`name:${name}`);
        }
      }
      map.set(sid, keys);
    }
    return map;
  });

  readonly canDocumentsView = computed(() =>
    this.hasAction(RA_SUBMODULE_IDS.DOCUMENTS, RA_ACTION_IDS.DOCUMENTS_VIEW, 'view'),
  );
  readonly canDocumentsManage = computed(() =>
    this.hasAction(RA_SUBMODULE_IDS.DOCUMENTS, RA_ACTION_IDS.DOCUMENTS_MANAGE, 'manage'),
  );

  readonly canFeedbackView = computed(() =>
    this.hasAction(RA_SUBMODULE_IDS.PROJECT_FEEDBACK, RA_ACTION_IDS.FEEDBACK_VIEW, 'view'),
  );
  readonly canFeedbackManage = computed(() =>
    this.hasAction(RA_SUBMODULE_IDS.PROJECT_FEEDBACK, RA_ACTION_IDS.FEEDBACK_MANAGE, 'manage'),
  );

  readonly canDirectoryView = computed(() =>
    this.hasAction(RA_SUBMODULE_IDS.DIRECTORY, RA_ACTION_IDS.DIRECTORY_VIEW, 'view'),
  );
  readonly canDirectoryManage = computed(() =>
    this.hasAction(RA_SUBMODULE_IDS.DIRECTORY, RA_ACTION_IDS.DIRECTORY_MANAGE, 'manage'),
  );

  readonly canNotesView = computed(() =>
    this.hasAction(RA_SUBMODULE_IDS.ADDITIONAL_NOTES, RA_ACTION_IDS.NOTES_VIEW, 'view'),
  );
  readonly canNotesManage = computed(() =>
    this.hasAction(RA_SUBMODULE_IDS.ADDITIONAL_NOTES, RA_ACTION_IDS.NOTES_MANAGE, 'manage'),
  );

  readonly canInsightsView = computed(() =>
    this.hasAction(RA_SUBMODULE_IDS.INSIGHTS, RA_ACTION_IDS.INSIGHTS_VIEW, 'view'),
  );

  /** RA module access (View). Inbox Issues tab — no dedicated Issues action id. */
  readonly canIssuesView = computed((): boolean => {
    if (this._isAdmin()) {
      return true;
    }
    const modules = this._permissions()?.modules ?? [];
    const ra = modules.find((module) => this.isResourceAllocationModule(module));
    return ra?.hasAccess === true;
  });

  /** Existing RA Manage actions (Documents / Feedback / Directory / Notes). */
  readonly canIssuesManage = computed((): boolean => {
    if (this._isAdmin()) {
      return true;
    }
    return (
      this.canDocumentsManage() ||
      this.canFeedbackManage() ||
      this.canDirectoryManage() ||
      this.canNotesManage()
    );
  });

  /**
   * Whether a project-details tab is allowed by Resource Allocation view access.
   * Non-gated tabs (Work Plan, Allocation, Summary, etc.) always return true.
   */
  canViewTab(tabKey: ProjectDetailsTabKey): boolean {
    const submoduleId = GATED_TAB_TO_SUBMODULE.get(tabKey);
    if (submoduleId == null) {
      return true;
    }
    if (this._isAdmin()) {
      return true;
    }
    switch (submoduleId) {
      case RA_SUBMODULE_IDS.DOCUMENTS:
        return this.canDocumentsView();
      case RA_SUBMODULE_IDS.PROJECT_FEEDBACK:
        return this.canFeedbackView();
      case RA_SUBMODULE_IDS.DIRECTORY:
        return this.canDirectoryView();
      case RA_SUBMODULE_IDS.ADDITIONAL_NOTES:
        return this.canNotesView();
      case RA_SUBMODULE_IDS.INSIGHTS:
        return this.canInsightsView();
      default:
        return false;
    }
  }

  /** Resolve a child route path to a gated tab key, if applicable. */
  resolveGatedTabFromRoute(routePath: string | null | undefined): ProjectDetailsTabKey | null {
    const key = String(routePath ?? '')
      .trim()
      .toLowerCase();
    return ROUTE_TO_GATED_TAB.get(key) ?? null;
  }

  private hasAction(submoduleId: number, actionId: number, actionName: string): boolean {
    if (this._isAdmin()) {
      return true;
    }
    const granted = this.grantedActionsBySubmodule().get(submoduleId);
    if (!granted || granted.size === 0) {
      return false;
    }
    return granted.has(`id:${actionId}`) || granted.has(`name:${actionName.toLowerCase()}`);
  }

  private isResourceAllocationModule(module: RawModule): boolean {
    const name = String(module.moduleName ?? '')
      .trim()
      .toLowerCase();
    if (name === 'resource allocation') {
      return true;
    }
    if (name) {
      return false;
    }
    const id = module.id ?? module.moduleId;
    return id === RESOURCE_ALLOCATION_MODULE_ID;
  }

  private resolveSubmoduleId(sub: RawSubModule): number | null {
    const rawName = String(sub.subModuleName ?? sub.submoduleName ?? sub.name ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, ' ');
    const compact = rawName.replace(/\s+/g, '');
    const byName = SUBMODULE_NAME_TO_ID[rawName] ?? SUBMODULE_NAME_TO_ID[compact];
    if (byName != null) {
      return byName;
    }
    const sid = sub.id ?? sub.submoduleId;
    if (sid != null && Number.isFinite(Number(sid))) {
      return Number(sid);
    }
    return null;
  }

  private resolveAdminBypass(): boolean {
    if (this.rbac.isAdmin()) {
      return true;
    }
    try {
      const raw = localStorage.getItem(PERMISSIONS_KEY);
      if (!raw) {
        return false;
      }
      const permissions = JSON.parse(raw) as { issystemRole?: boolean };
      return permissions?.issystemRole === true;
    } catch {
      return false;
    }
  }

  private readPermissions(): RawPermissions | null {
    try {
      const raw = localStorage.getItem(PERMISSIONS_KEY);
      return raw ? (JSON.parse(raw) as RawPermissions) : null;
    } catch {
      return null;
    }
  }
}
